import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { interval, Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuditService } from 'src/app/services/audit.service';

export interface LogEntry {
  id: string;
  timestamp: string;
  level: 'success' | 'error' | 'warn' | 'info';
  action: string;
  resource: string;
  user?: string;
  details?: Record<string, unknown>;
}

@Component({
  selector: 'app-logs-viewer',
  templateUrl: './logs-viewer.component.html',
  styleUrls: ['./logs-viewer.component.scss'],
})
export class LogsViewerComponent implements OnInit, OnDestroy {
  @ViewChild('logContainer') logContainer!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  logs: LogEntry[] = [];
  filteredLogs: LogEntry[] = [];
  totalLogs = 0;
  loading = false;
  error: string | null = null;
  liveMode = false;
  searchQuery = '';
  activeFilters: Set<string> = new Set();
  expandedLogs: Set<string> = new Set();

  private liveSubscription?: Subscription;
  private pollInterval = 5000;
  private searchSubject = new Subject<string>();

  readonly logLevels = ['success', 'error', 'warn', 'info'];

  constructor(private readonly auditService: AuditService) {}

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (event.key === '/' && document.activeElement?.tagName !== 'INPUT') {
      event.preventDefault();
      this.searchInput?.nativeElement?.focus();
    }
    if (event.key === 'Escape' && this.searchQuery) {
      this.searchQuery = '';
      this.loadLogs();
    }
  }

  ngOnInit() {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.loadLogs();
      });
    this.loadLogs();
  }

  ngOnDestroy() {
    this.liveSubscription?.unsubscribe();
  }

  loadLogs() {
    this.loading = true;

    // If only one filter selected, we could pass it to backend.
    // But backend only supports one. For now let's just fetch recent logs.
    const levelParam =
      this.activeFilters.size === 1
        ? Array.from(this.activeFilters)[0]
        : undefined;

    this.auditService.getLogs(1, 100, this.searchQuery, levelParam).subscribe({
      next: data => {
        this.logs = data.items.map(log => ({
          ...log,
          user: log.userEmail,
        }));
        this.totalLogs = data.meta.totalItems;
        this.loading = false;
        this.applyFilters(); // Apply client-side filters (especially if multiple levels selected or regex search needed)
      },
      error: err => {
        console.error(err);
        this.error = 'Failed to load logs';
        this.loading = false;
      },
    });
  }

  applyFilters() {
    // We already filtered by search query and level (if single) on backend.
    // However, to keep UI consistent and responsive, we can still filter the returned set.
    // Specially if multiple levels are selected.

    let result = this.logs;

    if (this.activeFilters.size > 0) {
      // If we fetched using level param, this is redundant but harmless.
      // If we didn't (because >1 selected), this is necessary.
      result = result.filter(log => this.activeFilters.has(log.level));
    }

    // Backend search might be simpler (ILIKE). Client side search is more comprehensive (JSON stringify).
    // If query was sent to backend, result is already filtered by it.
    // But let's refine it locally just in case.
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        log =>
          log.action.toLowerCase().includes(query) ||
          log.resource.toLowerCase().includes(query) ||
          log.user?.toLowerCase().includes(query) ||
          JSON.stringify(log.details).toLowerCase().includes(query)
      );
    }

    this.filteredLogs = result;
  }

  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  toggleFilter(level: string) {
    if (this.activeFilters.has(level)) {
      this.activeFilters.delete(level);
    } else {
      this.activeFilters.add(level);
    }
    this.loadLogs();
  }

  isFilterActive(level: string): boolean {
    return this.activeFilters.has(level);
  }

  clearFilters() {
    this.activeFilters.clear();
    this.searchQuery = '';
    this.loadLogs();
  }

  toggleLogExpand(logId: string) {
    if (this.expandedLogs.has(logId)) {
      this.expandedLogs.delete(logId);
    } else {
      this.expandedLogs.add(logId);
    }
  }

  isExpanded(logId: string): boolean {
    return this.expandedLogs.has(logId);
  }

  toggleLiveMode() {
    this.liveMode = !this.liveMode;
    if (this.liveMode) {
      this.liveSubscription = interval(this.pollInterval).subscribe(() =>
        this.loadLogs()
      );
    } else {
      this.liveSubscription?.unsubscribe();
    }
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return (
      date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }) +
      '.' +
      String(date.getMilliseconds()).padStart(3, '0')
    );
  }

  formatDate(timestamp: string): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    });
  }

  formatDetailsPretty(details: Record<string, unknown>): string {
    return JSON.stringify(details, null, 2);
  }

  hasDetails(details?: Record<string, unknown>): boolean {
    return !!details && Object.keys(details).length > 0;
  }

  getLineNumber(index: number): string {
    return String(index + 1).padStart(4, ' ');
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  hasActiveFilters(): boolean {
    return this.activeFilters.size > 0 || this.searchQuery.trim().length > 0;
  }

  trackByLogId(index: number, log: LogEntry): string {
    return log.id;
  }
}
